/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { DockerHost, getDockerHostContainers, getDockerInfo } from "@/api/docker";
import { StatusDot, fmtBytes } from "@/components/servers/kit";

const EnvironmentCard: React.FC<{ host: DockerHost; onSelect: () => void }> = ({ host, onSelect }) => {
  const { data: info, isError } = useQuery({
    queryKey: ["docker-info", host.id],
    queryFn: () => getDockerInfo(host.id),
    enabled: host.enabled,
    refetchInterval: 20_000,
    retry: false,
  });
  const { data: cached } = useQuery({
    queryKey: ["docker-host-containers", host.id],
    queryFn: () => getDockerHostContainers(host.id),
    enabled: host.enabled,
    refetchInterval: 30_000,
    retry: false,
  });
  const failing = cached?.filter((c) => c.state === "restarting" || c.state === "dead").length ?? 0;
  const state = !host.enabled ? "idle" : isError ? "down" : info ? (failing ? "warn" : "up") : "idle";
  const stopped = info ? info.stopped : 0;

  return (
    <button type="button" className={`dkr-env-card dkr-env-card--${state}`} onClick={onSelect}>
      <div className="dkr-env-top">
        <StatusDot state={state} />
        <b>{host.name}</b>
        <span className="dkr-dim">{!host.enabled ? "disabled" : isError ? "unreachable" : info ? `Docker ${info.serverVersion}` : "checking…"}</span>
      </div>
      <span className="dkr-mono dkr-dim dkr-trunc">{host.host}</span>
      <div className="dkr-env-stats">
        <div>
          <b className={info?.running ? "dkr-ok" : ""}>{info ? info.running : "—"}</b>
          <span>running</span>
        </div>
        <div>
          <b>{info ? stopped : "—"}</b>
          <span>stopped</span>
        </div>
        <div>
          <b className={failing ? "dkr-bad" : ""}>{cached ? failing : "—"}</b>
          <span>failing</span>
        </div>
        <div>
          <b>{info ? info.images : "—"}</b>
          <span>images</span>
        </div>
      </div>
      <div className="dkr-env-foot">
        <span>{info ? `${info.cpus} CPU · ${fmtBytes(info.memTotal, 0)} RAM` : " "}</span>
        <span>{info?.os ?? ""}</span>
      </div>
    </button>
  );
};

export const EnvironmentsHome: React.FC<{ hosts: DockerHost[]; onSelect: (h: DockerHost) => void }> = ({ hosts, onSelect }) => (
  <div className="dkr-envs">
    {hosts.map((h) => (
      <EnvironmentCard key={h.id} host={h} onSelect={() => onSelect(h)} />
    ))}
  </div>
);
